import { useMemo } from 'react';
import { TipoCampo } from 'utils/types/campo';
import { Campo } from 'utils/types/campo';
import { validaCnpj, validaCpf } from 'utils/formatUtils';
import * as Yup from 'yup';

const useGerarSchemaDinamico = (campos: Campo[]) => {
  return useMemo(() => {
    const shape: Record<string, any> = {};

    campos.forEach((campo) => {
      let schema: any;

      if (campo.tipoCampo === TipoCampo.number) {
        schema = Yup.string();

        if (campo.obrigatoriedade) {
          schema = schema.required(`${campo.descricao} é obrigatório`);
        } else {
          schema = schema.notRequired().nullable();
        }

        schema = schema.test(
          'numero-valido',
          `${campo.descricao} deve ser um número válido`,
          (value: string) => {
            if (!value) return true;
            return /^-?\d+(,\d+)?$/.test(value); // Aceita número com ou sem vírgula
          },
        );

        const temDecimal = campo.decimal != null;
        const temMax = campo.tamanhoMaximo > 0;

        if (campo.tamanhoMinimo > 0) {
          schema = schema.test(
            'min-length',
            `${campo.descricao} deve ter no mínimo ${campo.tamanhoMinimo} caracteres`,
            (value: string | null | undefined) => {
              if (!value) return true;
              return value.length >= campo.tamanhoMinimo;
            },
          );
        }

        // Valida número com decimal
        if (temDecimal && temMax) {
          const decimalPlaces = campo.decimal ?? 0;
          const inteiroMaxLength = campo.tamanhoMaximo - 1 - decimalPlaces;
          const maxInteiro = 10 ** inteiroMaxLength - 1;
          const maxValor = parseFloat(
            `${maxInteiro}.${'9'.repeat(decimalPlaces)}`,
          );

          schema = schema.test(
            'max-decimal-completo',
            `${campo.descricao} deve ter no máximo ${inteiroMaxLength} dígitos na parte inteira e ${campo.decimal} na parte decimal`,
            (value?: string) => {
              if (!value) return true;

              const [inteiroRaw = '', decimalRaw = ''] = value
                .replace('.', ',')
                .split(',');
              const inteiroLimpo = inteiroRaw.replace(/^[-0]+/, '') || '0';
              if (inteiroLimpo.length > inteiroMaxLength) return false;
              if (decimalRaw.length > (campo.decimal ?? 0)) return false;

              const valorNumerico = parseFloat(
                value.replace(',', '.').replace(/[^0-9.-]/g, ''),
              );
              return valorNumerico <= maxValor;
            },
          );
        }

        // Valida número sem decimal
        if (!temDecimal && temMax) {
          schema = schema.test(
            'max-inteiro',
            `${campo.descricao} deve ter no máximo ${campo.tamanhoMaximo} dígitos`,
            (value?: string) => {
              if (!value) return true;
              const inteiro = value.replace(/\D/g, '');
              return inteiro.length <= campo.tamanhoMaximo;
            },
          );
        }
      } else {
        schema = Yup.string();

        if (campo.obrigatoriedade) {
          schema = schema.required(`${campo.descricao} é obrigatório`);
        } else {
          schema = schema.notRequired();
        }

        const isCnpjOrCpf =
          campo.referencia.toLowerCase().includes('cnpj') ||
          campo.referencia.toLowerCase().includes('cpf') ||
          campo.mascara === '00.000.000/0000-00';

        if (isCnpjOrCpf) {
          schema = schema.test(
            'documento-valido',
            `${campo.descricao} inválido`,
            (value?: string | null) => {
              if (!value) return true;
              const limpo = value.replace(/[^a-zA-Z0-9]/g, '');
              if (limpo.length === 14) return validaCnpj(limpo);
              if (limpo.length === 11) return validaCpf(limpo);
              return false;
            },
          );
        }

        if (campo.tamanhoMinimo > 0 && campo.tipoCampo !== TipoCampo.date) {
          if (campo.mascara) {
            schema = schema.transform((value: string) =>
              value?.replace(/[^\w]/g, ''),
            );
          }
          schema = schema.min(
            campo.tamanhoMinimo,
            `${campo.descricao} deve ter no mínimo ${campo.tamanhoMinimo} caracteres`,
          );
        }

        if (campo.tamanhoMaximo > 0 && campo.tipoCampo !== TipoCampo.date) {
          if (campo.mascara) {
            schema = schema.transform((value: string) =>
              value?.replace(/[^\w]/g, ''),
            );
          }
          schema = schema.max(
            campo.tamanhoMaximo,
            `${campo.descricao} deve ter no máximo ${campo.tamanhoMaximo} caracteres`,
          );
        }
      }

      shape[campo.referencia] = schema;
    });

    return Yup.object().shape(shape);
  }, [campos]);
};

export default useGerarSchemaDinamico;
